<script setup lang="ts">
import { PhFolder } from '@phosphor-icons/vue';
import type { Audience } from 'cesg-shared/dist/models';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ModalTransition } from '../modal-transition';
import { mountDisplayName } from '../../logic/core/useFileManagerMounts';
import { useFileManagerTransfer, type PendingTransfer } from '../../logic/transfer/useFileManagerTransfer';
import { describeItems } from '../../utils';
import { getFileIcon } from '../../views/file-explorer-view/sub-components/render-modes/_shared/file-object/sub-components/file-item/get-file-icon';
import type { FileSystemItem } from '../../types';

const props = defineProps<{
  transfer: PendingTransfer;
}>();

const { t } = useI18n();

// A sibling of DeleteConfirmModal rather than a shared generic ConfirmModal: the two share a shell,
// not a meaning, and hoisting an abstraction out of a sample size of two tends to produce a
// component with six props neither caller fully uses. Self-injects the transfer state for its
// actions for the same reason DeleteConfirmModal does — confirmTransfer/cancelTransfer already live
// there, so there is nothing to thread through FileManager.vue.
const { confirmTransfer, cancelTransfer } = useFileManagerTransfer();

const breakdown = computed(() => describeItems(props.transfer.items));

// NOTE: no LIBRARY is named in here — not 'CESG', not 'Delad'. The destination comes from the mount's
// own display name and the disclosure from the policy's gainedAudiences, so a third library gets
// correct wording with no change here. A third *audience* is the one thing that does need a change, and
// AUDIENCE_LABEL_KEYS below is written so the compiler asks for it.
const destination = computed(() =>
  mountDisplayName(props.transfer.destinationMount),
);

const title = computed(() =>
  t('fileManager.transferConfirmModal.title', { destination: destination.value }),
);

// Single item: name it. The whole point of this dialog is "are you sure about THIS file", and
// "1 fil" is strictly less reassuring than the filename. Beyond one, describe the mix — same
// convention deleteMenuLabel/DeleteConfirmModal already established.
const summary = computed(() => {
  const { total, fileCount, directoryCount } = breakdown.value;
  if (total === 1) {
    return t('fileManager.transferConfirmModal.summarySingle', {
      name: props.transfer.items[0].name,
      destination: destination.value,
    });
  }
  if (fileCount > 0 && directoryCount > 0) {
    return t('fileManager.transferConfirmModal.summaryMixed', {
      fileCount,
      directoryCount,
      destination: destination.value,
    });
  }
  if (directoryCount > 0) {
    return t('fileManager.transferConfirmModal.summaryFoldersOnly', {
      directoryCount,
      destination: destination.value,
    });
  }
  return t('fileManager.transferConfirmModal.summaryFilesOnly', {
    fileCount,
    destination: destination.value,
  });
});

/**
 * How each audience is named in the disclosure sentence. A Record over the union, not
 * `t(\`fileManager.audience.${audience}\`)`: `Audience` is declared in cesg-shared, so a dynamic key
 * would let a new member (a 'PARTNER' tier, say) compile everywhere and then render the literal
 * string "fileManager.audience.PARTNER" — in the one sentence on the screen whose entire job is
 * telling someone who is about to be able to read their files. This makes that a build failure until
 * the audience has a name.
 */
const AUDIENCE_LABEL_KEYS: Record<Audience, string> = {
  CESG: 'fileManager.audience.CESG',
  GUEST: 'fileManager.audience.GUEST',
};

// "A", "A och B", "A, B och C" — the last pair joined differently from the rest, rather than one
// separator repeated. Three audiences cannot happen today (there are two), but the alternative is a
// sentence that degrades the moment a third exists, and this costs three lines.
const formatAudienceList = (labels: string[]): string => {
  if (labels.length < 2) return labels.join('');
  const last = labels[labels.length - 1];
  const rest = labels.slice(0, -1).join(t('fileManager.transferConfirmModal.audienceListSeparator'));
  return rest + t('fileManager.transferConfirmModal.audienceSeparator') + last;
};

// Rendered from the policy's gained audiences, and omitted entirely when that set is empty — the
// modal only ever opens when it is non-empty today, but the emptiness check is what keeps the
// component honest if a future policy opens it for some other reason.
const disclosure = computed(() => {
  const { gainedAudiences } = props.transfer.policy;
  if (gainedAudiences.length === 0) return '';
  const audiences = formatAudienceList(
    gainedAudiences.map((audience) => t(AUDIENCE_LABEL_KEYS[audience])),
  );
  return t('fileManager.transferConfirmModal.disclosure', { audiences });
});

const itemIcon = (item: FileSystemItem) => {
  if (item.type !== 'file') {
    return { component: PhFolder, color: 'var(--primary-color)' };
  }
  const fileIcon = getFileIcon(item.name);
  return { component: fileIcon.icon, color: fileIcon.color };
};
</script>

<template>
  <ModalTransition>
    <div class="modal is-active" role="dialog" aria-modal="true">
      <div class="modal-background" @click="cancelTransfer"></div>

      <div class="modal-card transfer-confirm-modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ title }}</p>
          <button
            class="delete"
            :aria-label="t('fileManager.transferConfirmModal.closeAriaLabel')"
            type="button"
            @click="cancelTransfer"
          ></button>
        </header>

        <section class="modal-card-body">
          <p class="summary-text">{{ summary }}</p>
          <p v-if="disclosure" class="disclosure-text">{{ disclosure }}</p>
          <ul class="transfer-target-list">
            <li v-for="item in transfer.items" :key="item.path" class="transfer-target-row">
              <component :is="itemIcon(item).component" :size="16" :color="itemIcon(item).color" />
              <span class="transfer-target-name" :title="item.name">{{ item.name }}</span>
            </li>
          </ul>
        </section>

        <footer class="modal-card-foot actions">
          <button type="button" class="button is-light" @click="cancelTransfer">
            {{ t('fileManager.transferConfirmModal.cancel') }}
          </button>
          <!-- Neutral, not is-danger: a copy adds a file and removes nothing. Borrowing delete's red
               (and its countdown) would train users to read the two dialogs as equally severe, which
               blunts the one that actually destroys data. -->
          <button type="button" class="button is-primary" @click="confirmTransfer">
            {{ t('fileManager.transferConfirmModal.confirm') }}
          </button>
        </footer>
      </div>
    </div>
  </ModalTransition>
</template>

<style scoped lang="scss">
.transfer-confirm-modal-card {
  width: min(460px, 92vw);

  .modal-card-head,
  .modal-card-foot {
    padding: 0.75rem 1.5rem;
  }

  .modal-card-title {
    font-size: 1.1rem;
  }

  .delete {
    height: 24px;
    width: 24px;
    max-height: 24px;
    max-width: 24px;
  }
}

.actions {
  justify-content: flex-end;
  gap: 1rem;
}

.summary-text {
  margin-bottom: 0.5rem;
  font-size: 0.9375rem;
}

/* The one line that carries the consequence — given weight without the alarm of an error colour. */
.disclosure-text {
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-left: 3px solid var(--text-warning, #d97706);
  background-color: rgba(217, 119, 6, 0.08);
  font-size: 0.875rem;
}

/* Capped height + scroll rather than letting the modal grow unbounded — a large multi-selection
   should still fit on screen, not push the footer off it. */
.transfer-target-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-height: 220px;
  padding: 0.5rem;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.5rem;
}

.transfer-target-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.transfer-target-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
